import type {
  PublicInvite,
  PublicRsvpRespondInput,
  PublicSeatInfo,
  PublicSelfRsvpInput,
  PublicSelfRsvpResult,
} from '@peles/shared';

/**
 * Public (no-auth) RSVP API. Uses plain fetch — the authenticated apiClient
 * would redirect to /login on 401 and attach producer tokens, neither of
 * which applies to the tokenized guest link.
 */

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = 'Request failed';
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      // keep default message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function getPublicInvite(token: string): Promise<PublicInvite> {
  const res = await fetch(`/api/v1/rsvp/${encodeURIComponent(token)}`);
  return handle<PublicInvite>(res);
}

export interface PublicRsvpResult {
  response: string;
  adultCount: number;
  kidCount: number;
  notes: string | null;
}

export async function respondPublicInvite(
  token: string,
  input: PublicRsvpRespondInput,
): Promise<PublicRsvpResult> {
  const res = await fetch(`/api/v1/rsvp/${encodeURIComponent(token)}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handle<PublicRsvpResult>(res);
}

/**
 * RSVP from the guest-less share link. The responder identifies themselves,
 * joins the event's guest list, and gets their own invitation token back to
 * continue on.
 */
export async function selfRsvpPublicInvite(
  token: string,
  input: PublicSelfRsvpInput,
): Promise<PublicSelfRsvpResult> {
  const res = await fetch(`/api/v1/rsvp/${encodeURIComponent(token)}/self-rsvp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handle<PublicSelfRsvpResult>(res);
}

export async function getPublicSeat(token: string): Promise<PublicSeatInfo> {
  const res = await fetch(`/api/v1/rsvp/${encodeURIComponent(token)}/seat`);
  return handle<PublicSeatInfo>(res);
}
